import { isActionOf } from "typesafe-actions";

import { hideToast, showToast } from "app/actions/toast_actions";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import type { ContactsDataSource } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext.types";
import { updateUsedContactsQuota } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";
import { exportContactRevealService } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/services/exportContactRevealService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { updateNotRevealedContactsIds, updateRevealedContactsByUniqId } from "../../common/store/action-creators";
import { createContactRevealByIdDto } from "../helpers/dto";
import { EXPORT_SUCCESS_TAG } from "../helpers/exportFlow";
import trackingService from "../services/tracking/trackingService";
import type {
    RevealResult,
    ContactRevealByIdRequestDto,
    RevealedContactDto,
    RevealedContactsResponseDto,
    TopNRevealRequestDto,
} from "../types/dto";

import * as ac from "./action-creators";
import { multipleContactsWereRevealedAsync } from "./action-creators";
import { selectLeadsSearchContactsSearchRequestDto } from "./selectors";

export function saveMultipleContactsRevealedThunk(
    response: RevealedContactsResponseDto,
    revealedContactsCount: number,
    requestedContactsQuantity: number,
    isImpersonate: boolean,
) {
    return (dispatch: ThunkDispatchCommon) => {
        const { contacts = [], notRevealedContactsIds = [] } = response;

        if (notRevealedContactsIds.length > 0) {
            dispatch(updateNotRevealedContactsIds(notRevealedContactsIds));
        }

        if (contacts.length > 0) {
            dispatch(updateRevealedContactsByUniqId(contacts));
        }

        if (revealedContactsCount > 0) {
            trackingService.trackContactsBulkRevealSuccess(revealedContactsCount, requestedContactsQuantity);
        }

        if (revealedContactsCount !== requestedContactsQuantity) {
            trackingService.trackContactsBulkRevealFailure(
                requestedContactsQuantity - revealedContactsCount,
                requestedContactsQuantity,
            );
        }

        dispatch(updateUsedContactsQuota(isImpersonate ? 0 : revealedContactsCount));

        return dispatch(ac.multipleContactsWereRevealedAsync.success(revealedContactsCount));
    };
}

function revealLeadsSearchTopNContactsThunk(n: number) {
    return async function revealTopNContacts(
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.multipleContactsWereRevealedAsync.request());

        const { createToast, getToastTitle, getErrorToastTitle, getToastComponent } = exportContactRevealService;
        const toast = createToast(getToastTitle(n));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        try {
            const state = getState();
            const activeSearchLeadsType = selectActiveLeadsType(state);

            const searchParams = selectLeadsSearchContactsSearchRequestDto(state);
            const dto: TopNRevealRequestDto = {
                ...searchParams,
                top: n,
            };
            const response = await deps.si.api.contactsSearch.revealTopNContacts(dto, activeSearchLeadsType);
            let revealedContactsCount = response.uniqueCount;

            if (response.requestId) {
                const statusResponseCount = await deps.si.api.contactsSearch.revealStatus(response.requestId);
                revealedContactsCount += statusResponseCount;
            }

            const resultAction = dispatch(
                saveMultipleContactsRevealedThunk(
                    response,
                    revealedContactsCount,
                    n,
                    state.impersonation.impersonateMode,
                ),
            );

            dispatch(hideToast(toast));

            return { resultAction, toast, response };
        } catch (e) {
            trackingService.trackContactsBulkRevealFailure(n, n);
            dispatch(hideToast(toast));
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            return {
                resultAction: dispatch(ac.multipleContactsWereRevealedAsync.failure(e?.message)),
                toast,
                response: undefined,
            };
        }
    };
}

/**
 * The API call to reveal multiple contact entries
 * @param dto
 * @param revealMultiple - Page-specific reveal API resolved from ContactsFlowStrategy
 */
function revealMultipleContactsByIdThunk(
    dto: readonly ContactRevealByIdRequestDto[],
    revealMultiple: ContactsDataSource["revealMultiple"],
    isSilent?: boolean,
) {
    return async function revealMultipleContactsByPairs(
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.multipleContactsWereRevealedAsync.request());

        const { createToast, getToastTitle, getErrorToastTitle, getToastComponent } = exportContactRevealService;
        const toast = createToast(getToastTitle(dto.length));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));
        try {
            const response = await revealMultiple(dto);
            let revealedContactsCount = response.uniqueCount;

            if (response.requestId) {
                const statusResponseCount = await deps.si.api.contacts.revealStatus(response.requestId);
                revealedContactsCount += statusResponseCount;
            }

            const resultAction = await dispatch(
                saveMultipleContactsRevealedThunk(
                    response,
                    revealedContactsCount,
                    dto.length,
                    getState().impersonation.impersonateMode,
                ),
            );

            dispatch(hideToast(toast));

            return { resultAction, response };
        } catch (e) {
            trackingService.trackContactsBulkRevealFailure(dto.length, dto.length);
            dispatch(hideToast(toast));
            if (!isSilent) {
                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            }
            return {
                resultAction: dispatch(ac.multipleContactsWereRevealedAsync.failure(e?.message)),
                response: undefined,
            };
        }
    };
}

export function showMultipleRevealResultToastThunk(
    resultAction:
        | ReturnType<typeof ac.multipleContactsWereRevealedAsync.success>
        | ReturnType<typeof ac.multipleContactsWereRevealedAsync.failure>,
    numberOfTotalContacts: number,
) {
    return async function showMultipleRevealResultToast(dispatch: ThunkDispatchCommon) {
        const { getErrorToastTitle, getSuccessToastTitle } = exportContactRevealService;
        if (isActionOf(ac.multipleContactsWereRevealedAsync.success)(resultAction)) {
            const { payload } = resultAction;

            if (payload === 0) {
                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
                return;
            }

            return dispatch(
                exportToExcelToastThunk(getSuccessToastTitle(payload, numberOfTotalContacts), EXPORT_SUCCESS_TAG),
            );
        } else if (isActionOf(ac.multipleContactsWereRevealedAsync.failure)(resultAction)) {
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
        }
    };
}

/**
 * The API call to reveal multiple contact entries
 * @param dto
 * @param revealMultiple - Page-specific reveal API from ContactsFlowStrategy
 */
export function revealMultipleContactsByIdWithToastThunk(
    dto: readonly ContactRevealByIdRequestDto[],
    revealMultiple: ContactsDataSource["revealMultiple"],
) {
    return async function revealMultipleContactsByIdWithToast(dispatch: ThunkDispatchCommon) {
        const { resultAction } = await dispatch(revealMultipleContactsByIdThunk(dto, revealMultiple));

        return dispatch(showMultipleRevealResultToastThunk(resultAction, dto.length));
    };
}

export function revealLeadsSearchTopNContactsWithToastThunk(n: number) {
    return async function revealLeadsSearchTopNContactsWithToast(dispatch: ThunkDispatchCommon) {
        const { resultAction } = await dispatch(revealLeadsSearchTopNContactsThunk(n));

        dispatch(showMultipleRevealResultToastThunk(resultAction, n));
    };
}

/**
 * Silent reveal for multiple contacts by ID - returns result without showing toast.
 * Used when reveal is part of a combined reveal+export flow.
 */
export function revealMultipleContactsByIdSilentThunk(
    dto: readonly ContactRevealByIdRequestDto[],
    revealMultiple: ContactsDataSource["revealMultiple"],
) {
    return async function revealMultipleContactsByIdSilent(dispatch: ThunkDispatchCommon): Promise<RevealResult> {
        const { resultAction, response } = await dispatch(revealMultipleContactsByIdThunk(dto, revealMultiple, true));

        const revealedCount = isActionOf(multipleContactsWereRevealedAsync.success)(resultAction)
            ? resultAction.payload
            : 0;

        const needsRefetch = Boolean(response?.requestId);

        // For large batch reveals (requestId), the response may not contain individual contacts,
        // but the reveal is complete (revealStatus was already awaited). Use the dto IDs as revealed IDs.
        const revealedContactIds = needsRefetch
            ? dto.map((d) => d.contactId)
            : response?.contacts?.map((c: RevealedContactDto) => c.id);

        return { revealedCount, requestedCount: dto.length, revealedContactIds, needsRefetch };
    };
}

export function revealSelectedContactsByIdSilentThunk(
    uniqueIds: readonly string[],
    revealMultiple: ContactsDataSource["revealMultiple"],
) {
    return async function revealSelectedContactsByIdSilent(dispatch: ThunkDispatchCommon): Promise<RevealResult> {
        const revealDto = createContactRevealByIdDto(uniqueIds);
        return dispatch(revealMultipleContactsByIdSilentThunk(revealDto, revealMultiple));
    };
}
